import React, { useEffect, useState } from 'react';
import AppTitle from '../../../layouts/AppTitle';
import { useDispatch, useSelector } from 'react-redux';
import { getPageContent, submitForm } from '../../../../store/actions/ProductActions';
import { addFunctionsToCols } from '../../../../utils/appFunctions';
import { productsTable } from '../../../../utils/tableElements';
import AppDatagridTable from '../../../layouts/AppDatagridTable';
import AppDialogBox from '../../../pages/AppDialogBox';
import { Box, Button, Grid, Icon, IconButton, Paper, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { appMultipleOptions, productCategoryOptions } from '../../../../utils/appConstants';
import EditIcon from '@mui/icons-material/Edit';
import _ from 'lodash';

import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { toast } from 'react-toastify';

const ManageProducts = () => {
  const dispatch = useDispatch();

  const allProducts = useSelector(state => state?.products?.allProducts);
  const token = useSelector(state => state?.auth?.token);
  const btnLoaderOne = useSelector(state => state?.app?.btnLoaderOne);

  const [open, setOpen] = useState(false);
  const [actionId, setActionId] = useState();
  const [values, setValues] = useState({ category: productCategoryOptions[0] });
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [tableData, setTableData] = useState({});
  const [customizeModal, setCustomizeModal] = useState(false);
  const [customizeValues, setCustomizeValues] = useState({});
  const [variants, setVariants] = useState([]);
  const [editVariant, setEditVariant] = useState({});

  const defaultSubCategoryOptions = {
    Cakes: ['Cheesecake', 'Bento cake/Tub Cake'],
    'Plum cake': [
      'Rich plum cake',
      'Chocolate plum cake',
      'Eggless plum cake',
      'Non alchocolic plum cake',
      'Plum cake slices',
    ],
  };

  useEffect(() => {
    dispatch(getPageContent('getAllProducts'));
    addFunctionsToCols(productsTable, editProducts, 'editIcon');
    addFunctionsToCols(productsTable, deleteProducts, 'deleteIcon');
  }, []);

  useEffect(() => {
    if (values?.category) {
      const options = defaultSubCategoryOptions[values?.category];
      setSubCategoryOptions(options);
      setValues({ ...values, subCategory: options?.[0] });
    }
  }, [values?.category]);

  useEffect(() => {
    if (allProducts) {
      setTableData({ columns: productsTable, rows: allProducts });
      clearForm();
      setOpen(false);
    }
  }, [allProducts]);

  const handleOpenModal = () => {
    clearForm();
    setOpen(true);
  };

  const handleChange = e => {
    setValues({ ...values, [e?.target?.name]: e?.target?.value });
  };

  const handleChangeCustomizeValues = e => {
    setCustomizeValues({ ...customizeValues, [e?.target?.name]: e?.target?.value });
  };

  const handleModalClose = () => {
    setOpen(false);
  };

  function clearForm() {
    setValues({
      title: '',
      price: '',
      description: '',
      category: productCategoryOptions[0],
      badge: '',
      bufferingDays: '',
    });
    setActionId();
  }

  const handleSubmit = e => {
    e.preventDefault();
    const type = actionId ? 'editProduct' : 'createProduct';

    if (values?.isMultiplePrices && (!values?.priceOptions || values?.priceOptions?.length === 0)) {
      return toast.error('Price options are required.');
    }

    let submitValues = { additionalPath: values?.title, ...values };
    delete submitValues.isRecommended;
    dispatch(submitForm(type, token, submitValues, actionId));
  };

  const handleFileChange = e => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setValues({ ...values, files });
    }
  };

  function editProducts(row, col) {
    setValues({
      title: row?.title,
      price: row?.price,
      description: row?.description,
      category: row?.category,
      badge: row?.badge,
      subCategory: row?.subCategory,
      bufferingTime: row?.bufferingTime ?? [],
      isMultiplePrices: row?.isMultiplePrices ?? false,
      isSinglePrice: row?.isSinglePrice,
      priceOptions: row?.priceOptions ?? [],
      bufferingDays: row?.bufferingDays,
      customizeOptions: row?.customizeOptions,
      customizeCharges: row?.customizeCharges,
      selectLabel: row?.selectLabel,
    });
    setActionId(row?._id);
    setOpen(true);
  }

  function deleteProducts(row, col) {
    if (window.confirm('Are you sure you want to delete this product?')) {
      const submitValues = { isActive: false, isRecommended: false };
      dispatch(submitForm('editProduct', token, submitValues, row?._id));
    }
  }

  const handleAddBuffer = () => {
    if (!values?.days) {
      return alert('Days & hours are required');
    }

    let bufferingTime = values?.bufferingTime ?? [];

    let obj = { days: values?.days };

    if (values?.hours) {
      obj = { ...obj, hours: values?.hours };
    }

    bufferingTime = [...bufferingTime, obj];

    setValues({ ...values, bufferingTime, days: '', hours: '' });
  };

  const deleteBuffering = i => {
    let bufferingTime = values?.bufferingTime ?? [];
    delete bufferingTime[i];
    setValues({ ...values, bufferingTime, days: '', hours: '' });
  };

  const handleEditorChange = (event, editor) => {
    const data = editor.getData();
    setValues({ ...values, description: data });
  };

  const handleAddPrice = () => {
    if (values?.optionContent && values?.optionPrice) {
      const prevPriceOptions = values?.priceOptions ?? [];
      setValues({
        ...values,
        priceOptions: [
          ...prevPriceOptions,
          { price: values?.optionPrice, content: values?.optionContent },
        ],
        optionContent: '',
        optionPrice: '',
      });
    } else {
      return toast.error('Option price & content are required.');
    }
  };

  const deletePricingOption = i => {
    let pricingOption = values?.priceOptions ?? [];
    delete pricingOption[i];

    pricingOption = pricingOption?.filter(f => f.price);
    setValues({
      ...values,
      priceOptions: [...pricingOption],
    });
  };

  const handleAddCustomization = () => {
    setCustomizeValues({
      name: '',
    });
    setVariants([]);

    setCustomizeModal(true);
  };

  const handleCustomizationSubmit = e => {
    e.preventDefault();
    e.stopPropagation();
    const prevCustomizeOptions = values?.customizeOptions ?? [];
    let customizeOptions = [];

    if (customizeValues?.editId) {
      customizeOptions = prevCustomizeOptions?.map(c => {
        if (customizeValues?.editId === c?.cusId) {
          return {
            ...c,
            name: customizeValues?.name,
            variants,
          };
        }
        return c;
      });
    } else {
      customizeOptions = [
        ...prevCustomizeOptions,
        { name: customizeValues?.name, variants, cusId: prevCustomizeOptions?.length + 1 },
      ];
    }

    setValues({ ...values, customizeOptions });
    setCustomizeModal(false);
  };

  const handleVariants = () => {
    const { price, quantity } = customizeValues;
    if (!price || !quantity) {
      toast.error('Price & Quantity are required.');
      return;
    }

    if (editVariant?.cId) {
      const updatingVariants = variants?.map(v => {
        if (v?.cId === editVariant?.cId) {
          return { ...v, price, quantity };
        } else {
          return v;
        }
      });
      setVariants(updatingVariants);
      setEditVariant();
    } else {
      const newVariant = { price, quantity, cId: variants?.length + 1 };
      setVariants([...variants, newVariant]);
    }
    setCustomizeValues({ ...customizeValues, price: '', quantity: '' });
  };

  const handleDeleteVariant = v => {
    if (window.confirm('Are you sure you want to delete this variant?')) {
      const updatingVariants = variants?.filter(f => f?.cId != v?.cId);
      setVariants(updatingVariants);
    }
  };

  const handleEditVariant = variant => {
    const { price, quantity } = variant;
    setEditVariant(variant);
    setCustomizeValues({ ...customizeValues, price, quantity });
  };

  const handleCustomizationEdit = customization => {
    setCustomizeValues({ name: customization?.name, editId: customization?.cusId });

    setVariants(customization?.variants);

    setCustomizeModal(true);
  };

  return (
    <div>
      <AppTitle title='Product Management' from='Home' to='Products' />

      <div className='container my-5'>
        <div className='text-right mb-3'>
          <button className='btn btn-dark ' onClick={handleOpenModal}>
            Add Product
          </button>
        </div>

        <AppDatagridTable tableData={tableData} rowHeight={100} />

        <AppDialogBox
          title={'Customization caks'}
          setOpen={setCustomizeModal}
          open={customizeModal}
          size='sm'
          disableBackdropClick={false}
        >
          <form onSubmit={handleCustomizationSubmit}>
            <div className='form-group'>
              <label>Name *</label>
              <input
                type='text'
                placeholder='Name'
                className='form-control'
                required
                name='name'
                value={customizeValues?.name}
                onChange={handleChangeCustomizeValues}
              />
            </div>

            <Grid container flexGrow={1} spacing={1} mt={1}>
              {_.sortBy(variants, 'cId')?.map((variant, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Paper sx={{ bgcolor: '#f8f8f8', p: 1 }}>
                    <Typography variant='body1'>
                      <b>Quantity: </b>
                      {variant?.quantity}
                    </Typography>
                    <Typography variant='body1'>
                      <b>Price: </b>
                      {variant?.price}
                    </Typography>

                    <IconButton onClick={() => handleEditVariant(variant)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteVariant(variant)}>
                      <DeleteIcon />
                    </IconButton>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Paper sx={{ p: 2, bgcolor: '#f8f8f8', mt: 2 }}>
              <div className='form-group'>
                <label>Quantity</label>
                <input
                  type='text'
                  placeholder='Quantity. Eg: 1 kg'
                  className='form-control'
                  name='quantity'
                  value={customizeValues?.quantity}
                  onChange={handleChangeCustomizeValues}
                />
              </div>
              <div className='form-group'>
                <label>Pice</label>
                <input
                  type='number'
                  placeholder='Price'
                  className='form-control'
                  name='price'
                  value={customizeValues?.price}
                  onChange={handleChangeCustomizeValues}
                />
              </div>

              <Button onClick={handleVariants} variant='contained' size='small' sx={{ mt: 1 }}>
                {editVariant?.cId ? 'Edit' : 'Add'}
              </Button>
            </Paper>

            <Box my={1}>
              <Button variant='contained' size='small' sx={{ mr: 1 }} type='submit'>
                Submit
              </Button>

              <Button
                variant='contained'
                size='small'
                onClick={() => {
                  setCustomizeModal(false);
                }}
                color='error'
              >
                Cancel
              </Button>
            </Box>
          </form>
        </AppDialogBox>

        <AppDialogBox
          title={actionId ? 'Edit Product' : 'Add Product'}
          setOpen={setOpen}
          open={open}
          size='sm'
          disableBackdropClick={false}
        >
          <>
            <form onSubmit={handleSubmit}>
              <div className='form-group'>
                <label>Name *</label>
                <input
                  type='text'
                  placeholder='Product name'
                  className='form-control'
                  required
                  name='title'
                  value={values?.title}
                  onChange={handleChange}
                />
              </div>

              <div className='single-method form-check mt-3'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='accept_terms'
                  checked={values?.isMultiplePrices}
                  onChange={e => setValues({ ...values, isMultiplePrices: e?.target?.checked })}
                />
                <label className='form-check-label' for='accept_terms'>
                  Multiple Prices
                </label>
              </div>

              {values?.isMultiplePrices ? (
                <>
                  <hr />
                  <div className='form-group'>
                    <label>Option price</label>
                    <input
                      type='number'
                      placeholder='Product option price'
                      className='form-control'
                      name='optionPrice'
                      value={values?.optionPrice}
                      onChange={handleChange}
                    />
                  </div>

                  <div className='form-group'>
                    <label>Option content</label>
                    <input
                      type='text'
                      placeholder='Option content'
                      className='form-control'
                      name='optionContent'
                      value={values?.optionContent}
                      onChange={handleChange}
                    />
                  </div>

                  <Button sx={{ mt: 1 }} type='button' onClick={handleAddPrice}>
                    Add Price
                  </Button>

                  {values?.priceOptions && (
                    <>
                      <hr />
                      {values?.priceOptions?.map((data, i) => (
                        <div key={i} style={{ position: 'relative' }}>
                          <IconButton
                            type='button'
                            sx={{ position: 'absolute', top: '0%', right: '1%' }}
                            onClick={() => deletePricingOption(i)}
                          >
                            <DeleteIcon />
                          </IconButton>
                          <div style={{ display: 'flex' }}>
                            {i + 1}.{' '}
                            <div style={{ marginLeft: '5px' }}>
                              <p>
                                <b>Price: </b>
                                {data?.price}
                              </p>
                              <p>
                                <b>Content: </b>
                                {data?.content}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  <hr />
                </>
              ) : (
                <>
                  <div className='form-group'>
                    <label>Offer Price</label>
                    <input
                      type='number'
                      placeholder='Product offer price'
                      className='form-control'
                      name='offerPrice'
                      value={values?.offerPrice}
                      onChange={handleChange}
                    />
                  </div>

                  <div className='form-group'>
                    <label>Original Price *</label>
                    <input
                      type='number'
                      placeholder='Product price'
                      className='form-control'
                      required
                      name='price'
                      value={values?.price}
                      onChange={handleChange}
                    />
                  </div>
                </>
              )}

              <div className='form-group'>
                <label>Badge </label>
                <input
                  type='text'
                  placeholder='Badge'
                  className='form-control'
                  name='badge'
                  value={values?.badge}
                  onChange={handleChange}
                />
              </div>

              <div className='form-group'>
                <label>Select Category *</label>
                <div className='input-group'>
                  <select
                    className='form-control'
                    required
                    name='category'
                    value={values?.['category']}
                    onChange={handleChange}
                  >
                    {productCategoryOptions?.map((option, i) => (
                      <option value={option} key={i}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className='form-group'>
                <label>Select Sub Category</label>
                <div className='input-group'>
                  <select
                    className='form-control'
                    name='subCategory'
                    value={values?.['subCategory']}
                    onChange={handleChange}
                  >
                    {subCategoryOptions?.map((option, i) => (
                      <option value={option} key={i}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className='form-group'>
                <label>Description</label>

                <CKEditor
                  editor={ClassicEditor}
                  config={{
                    licenseKey: 'GPL', // Or 'GPL'.
                    // plugins: [ Essentials, Paragraph, Bold, Italic, FormatPainter ],
                    // toolbar: [ 'undo', 'redo', '|', 'bold', 'italic', '|', 'formatPainter' ],
                    initialData: values?.description ?? '',
                  }}
                  data={values?.description} // Initial content (if any)
                  onChange={handleEditorChange} // Handle changes
                />
              </div>

              <div className='form-group'>
                <label>Images (Should be less than 300kb)*</label>
                <input
                  type='file'
                  placeholder='Product price'
                  className='form-control'
                  // required={!actionId}
                  name='files'
                  multiple
                  onChange={handleFileChange}
                  accept='image/*'
                />
              </div>

              {/* <div className='form-group'>
                <div className='row'>
                  <div className='col-5'>
                    <label>Buffering Date</label>
                    <input
                      type='number'
                      placeholder='Days'
                      className='form-control'
                      name='days'
                      onChange={handleChange}
                      value={values?.days}
                    />
                  </div>
                  <div className='col-5'>
                    <label>Buffering Time</label>
                    <input
                      type='text'
                      placeholder='Time'
                      className='form-control'
                      name='hours'
                      onChange={handleChange}
                      value={values?.hours}
                    />
                  </div>
                  <div className='col-2 my-auto'>
                    <Button type='button' onClick={handleAddBuffer}>
                      Add
                    </Button>
                  </div>

                  <div>
                    {values?.bufferingTime &&
                      values?.bufferingTime?.map((d, i) => (
                        <div>
                          {d?.days} days {d?.hours && `${d?.hours} time`}
                          <IconButton type='button' onClick={() => deleteBuffering(i)}>
                            <DeleteIcon />
                          </IconButton>
                        </div>
                      ))}
                  </div>
                </div>
              </div> */}

              <div className='form-group'>
                <label>Buffering Days</label>
                <input
                  type='number'
                  placeholder='Minimum Buffering Days'
                  className='form-control'
                  required
                  name='bufferingDays'
                  value={values?.bufferingDays}
                  onChange={handleChange}
                />
              </div>

              {appMultipleOptions.includes(values?.['category']) && (
                <div className='single-method form-check mt-3'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='single_price'
                    checked={values?.isSinglePrice}
                    onChange={e => setValues({ ...values, isSinglePrice: e?.target?.checked })}
                  />
                  <label className='form-check-label' for='single_price'>
                    Single Price Product
                  </label>
                </div>
              )}

              {appMultipleOptions.includes(values?.['category']) && (
                <Box component='div' mt={1}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <b>Customize Options</b>
                    <button type='button' onClick={handleAddCustomization}>
                      Add
                    </button>
                  </Box>
                  <Box>
                    <ol>
                      {values?.customizeOptions?.map((option, i) => (
                        <li style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>{option?.name}</span>
                          <IconButton onClick={() => handleCustomizationEdit(option)}>
                            <EditIcon />
                          </IconButton>
                        </li>
                      ))}
                    </ol>
                  </Box>

                  <div className='form-group'>
                    <label>Customize Charges</label>
                    <input
                      type='number'
                      placeholder='Enter customization charges'
                      className='form-control'
                      name='customizeCharges'
                      value={values?.customizeCharges}
                      onChange={handleChange}
                    />
                  </div>
                </Box>
              )}

              <div className='form-group'>
                <label>Custom Select Label</label>
                <input
                  type='text'
                  placeholder='Default: Pick your flavor'
                  className='form-control'
                  name='selectLabel'
                  value={values?.selectLabel}
                  onChange={handleChange}
                />
              </div>

              <div className='text-right mt-2 mb-3'>
                <Button variant='contained' type='submit' disabled={btnLoaderOne}>
                  {btnLoaderOne ? 'Submitting...' : 'Submit'}
                </Button>

                <Button
                  variant='contained'
                  className='mx-2'
                  color='error'
                  onClick={handleModalClose}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </>
        </AppDialogBox>
      </div>
    </div>
  );
};

export default ManageProducts;
